import React, { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import {
  HubButton,
  HubHookFormSelect,
  HubHookFormSwitch,
  HubMaterialHookSelect,
  StationHookFormSelect,
} from "../../components";
import { StyledCell, StyledStack } from "./styles";
import { filterDipMovementsByLocation } from "../../constants/dips";
import { useStationListWithCache } from "../../components/StationHookFormSelect/useStationListWithCache";
import { getPreviousIds } from "./helper";
import ConvertedMinutesInput from "./ConvertedMinutesInput";
import { Typography } from "@mui/material";

const TableRowSx = {
  justifyItems: "center",
  ".DipRowButton": { display: "none" },
  "&:hover": { ".DipRowButton": { display: "inline" } },
};

const DipsTableRow = ({
  basePath,
  idx = 0,
  remove = () => {},
  getFieldProps = () => ({}),
  handleStationChange = () => {},
  locationId,
  ...rest
}) => {
  const row = `${basePath}.${idx}`;
  const cfg = `${row}.config`;

  const { control } = useFormContext();
  const dips = useWatch({ control, name: basePath }) || [];
  const stationId = useWatch({ control, name: `${row}.stationId` });
  const { stations = [] } = useStationListWithCache();

  const previousSands = useMemo(
    () => getPreviousIds(dips, idx, "config.materialId"),
    [dips, idx],
  );
  const previousSlurries = useMemo(
    () => getPreviousIds(dips, idx, "config.slurryId"),
    [dips, idx],
  );

  // Robot stations only see movements valid for the current location.
  // Hand stations see the unrestricted list (movement is informational then).
  const isRobot = useMemo(() => {
    const station = stations.find((s) => s.id === stationId);
    return !!station?.robotic;
  }, [stations, stationId]);

  const movementOptions = useMemo(
    () => filterDipMovementsByLocation(locationId),
    [locationId],
  );

  const cellAttrs = (col) => ({
    "data-dip-row": idx,
    "data-dip-col": col,
  });

  return (
    <StyledStack sx={TableRowSx} data-dip-row={idx} {...rest}>
      <StyledCell sx={{ width: "20px" }}>
        <Typography variant="caption">{idx + 1}</Typography>
      </StyledCell>
      <StyledCell
        {...cellAttrs(0)}
        sx={{
          "& .MuiFormControlLabel-root, .MuiFormControl-root": {
            width: "18px",
            gap: "0px",
          },
        }}
      >
        <HubHookFormSwitch
          name={`${cfg}.prewet`}
          key={`${cfg}.prewet`}
          shouldUnregister={false}
          isCheckbox
        />
      </StyledCell>
      <HubMaterialHookSelect
        name={`${cfg}.materialId`}
        key={`${cfg}.materialId`}
        showOnlyGroup="Sands"
        previousItems={previousSands}
        {...getFieldProps(`${cfg}.materialId`)}
        shouldUnregister={false}
        useDefault={false}
        required={false}
        hideLabel
        formControlProps={cellAttrs(1)}
      />
      <HubMaterialHookSelect
        name={`${cfg}.slurryId`}
        key={`${cfg}.slurryId`}
        {...getFieldProps(`${cfg}.slurryId`)}
        shouldUnregister={false}
        showOnlyGroup="Slurry"
        previousItems={previousSlurries}
        useDefault={false}
        hideLabel
        required
        formControlProps={cellAttrs(2)}
      />
      <HubHookFormSelect
        name={`${cfg}.movementId`}
        key={`${cfg}.movementId-${isRobot ? `r-${locationId}` : "h"}`}
        {...getFieldProps(`${cfg}.movementId`)}
        shouldUnregister={false}
        options={movementOptions}
        required={isRobot}
        allowNull={!isRobot}
        useDefault={isRobot}
        formControlProps={cellAttrs(3)}
      />
      <ConvertedMinutesInput
        name={`${row}.cycleTime`}
        key={`${row}.cycleTime`}
        divisor={60}
        endAdornmentText="hrs"
        required
        formControlProps={cellAttrs(4)}
      />
      <ConvertedMinutesInput
        name={`${row}.leadTime`}
        key={`${row}.leadTime`}
        divisor={1440}
        endAdornmentText="dys"
        required
        formControlProps={cellAttrs(5)}
      />
      <StationHookFormSelect
        name={`${row}.stationId`}
        key={`${row}.stationId`}
        shouldUnregister={false}
        department={2}
        {...getFieldProps(`${row}.stationId`)}
        onChange={(e) => handleStationChange(row, e.target.value)}
        hideLabel
        required
        formControlProps={cellAttrs(6)}
      />
      <StyledCell>
        <HubButton
          className="DipRowButton"
          icon="Trash2"
          variant="text"
          iconProps={{ size: 14 }}
          sx={{ color: "hubColors.redMain" }}
          onClick={() => remove(idx)}
        />
      </StyledCell>
    </StyledStack>
  );
};

export default DipsTableRow;
